import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/integrations/api/client";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/reports/user-audit-log")({
  component: UserAuditLogReport,
});

function UserAuditLogReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "user-audit-log", currentBranchId],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (currentBranchId) p.set("branch", currentBranchId);
      const res = await apiGet(`/reports/user-audit-log/?${p.toString()}`);
      return res as any[];
    }
  });

  const exportRows = useMemo(() => () => (data ?? []).map((r) => ({
    "Timestamp": format(new Date(r.timestamp), "PPpp"),
    "User": r.user,
    "Action": r.action,
    "Entity": r.model_name,
    "Changes": JSON.stringify(r.changes),
  })), [data]);

  return (
    <ReportShell
      id="R-26"
      title="User Activity & Audit Log Extract"
      description="Who-did-what change history and user activity."
      filters={filters}
      onFiltersChange={setFilters}
      onExport={exportRows}
      exportFilename="User_Audit_Log"
      allowedFilters={["branchId"]}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No data found</TableCell></TableRow>
                )}
                {data?.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap">{format(new Date(r.timestamp), "PPpp")}</TableCell>
                    <TableCell>{r.user}</TableCell>
                    <TableCell>{r.action}</TableCell>
                    <TableCell>{r.model_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
